import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  CalendarIcon, 
  DocumentTextIcon, 
  ExclamationTriangleIcon,
  PaperClipIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import leaveService from '../../services/leaveService';

// Validation schema function
const createValidationSchema = (editMode = false) => yup.object().shape({
  leaveTypeId: yup.number().positive('Please select a leave type').required('Leave type is required'),
  startDate: editMode 
    ? yup.date().required('Start date is required')
    : yup.date().required('Start date is required').min(new Date(), 'Start date cannot be in the past'),
  endDate: yup.date()
    .required('End date is required')
    .test('is-after-start', 'End date must be after start date', function(value) {
      const { startDate } = this.parent;
      return !startDate || !value || new Date(value) >= new Date(startDate);
    }),
  reason: yup.string().min(10, 'Please provide a detailed reason (minimum 10 characters)').max(500, 'Reason is too long').required('Reason is required'),
  isEmergency: yup.boolean(),
  emergencyReason: yup.string().when('isEmergency', {
    is: true,
    then: (schema) => schema.required('Emergency reason is required').min(10, 'Please provide detailed emergency reason'),
    otherwise: (schema) => schema.notRequired()
  }),
  handoverNotes: yup.string().max(1000, 'Handover notes are too long'),
  contactDuringLeave: yup.string().max(200, 'Contact information is too long')
});

const LeaveRequestForm = ({ leaveRequest = null, onSubmit, onCancel, isEdit = false, editMode = false, initialData = null }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [calculatedDays, setCalculatedDays] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(createValidationSchema(editMode)),
    defaultValues: {
      isEmergency: false,
      ...leaveRequest && {
        leaveTypeId: leaveRequest.leaveTypeId,
        startDate: leaveRequest.startDate ? new Date(leaveRequest.startDate).toISOString().split('T')[0] : '',
        endDate: leaveRequest.endDate ? new Date(leaveRequest.endDate).toISOString().split('T')[0] : '',
        reason: leaveRequest.reason,
        isEmergency: leaveRequest.isEmergency,
        emergencyReason: leaveRequest.emergencyReason,
        handoverNotes: leaveRequest.handoverNotes,
        contactDuringLeave: leaveRequest.contactDuringLeave
      }
    }
  });

  // Watch form values for calculations
  const watchedValues = watch(['startDate', 'endDate', 'leaveTypeId', 'isEmergency']);

  // Load leave types and balance
  useEffect(() => {
    const loadData = async () => {
      try {
        const [typesResponse, balanceResponse] = await Promise.all([
          leaveService.getLeaveTypes(),
          leaveService.getLeaveBalance()
        ]);
        
        setLeaveTypes(typesResponse.data || []);
        setLeaveBalance(balanceResponse.data || []);
      } catch (error) {
        console.error('Error loading leave data:', error);
        toast.error('Failed to load leave types and balance');
      }
    };

    loadData();
  }, []);

  // Populate form with initial data when editing
  useEffect(() => {
    if (editMode && initialData) {
      // Format dates for input fields (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      reset({
        leaveTypeId: initialData.leaveTypeId,
        startDate: formatDateForInput(initialData.startDate),
        endDate: formatDateForInput(initialData.endDate),
        reason: initialData.reason || '',
        isEmergency: initialData.isEmergency || false,
        emergencyReason: initialData.emergencyReason || '',
        handoverNotes: initialData.handoverNotes || '',
        contactDuringLeave: initialData.contactDuringLeave || ''
      });
    }
  }, [editMode, initialData, reset]);

  // Calculate leave days when dates change
  useEffect(() => {
    const [startDate, endDate] = watchedValues;
    if (startDate && endDate) {
      const days = leaveService.calculateLeaveDays(startDate, endDate);
      setCalculatedDays(days);
    } else {
      setCalculatedDays(0);
    }
  }, [watchedValues]);

  // Get selected leave type details
  const selectedLeaveType = leaveTypes.find(type => type.id === parseInt(watchedValues[0]));
  const selectedBalance = leaveBalance.find(balance => balance.leaveType.id === parseInt(watchedValues[0]));

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  // Remove selected file
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle form submission
  const onFormSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Add files to the data
      const submitData = {
        ...data,
        attachments: selectedFiles
      };

      await onSubmit(submitData);
      
      if (!isEdit) {
        reset();
        setSelectedFiles([]);
        setCalculatedDays(0);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if request exceeds available balance
  const exceedsBalance = selectedBalance && calculatedDays > selectedBalance.daysRemaining;

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-lg rounded-lg overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
            {editMode || isEdit ? 'Edit Leave Request' : 'Submit Leave Request'}
          </h3>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          {/* Leave Type and Emergency Toggle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Leave Type *
              </label>
              <select
                {...register('leaveTypeId')}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.leaveTypeId ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Leave Type</option>
                {leaveTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.maxDaysPerYear} days/year)
                  </option>
                ))}
              </select>
              {errors.leaveTypeId && (
                <p className="mt-1 text-sm text-red-600">{errors.leaveTypeId.message}</p>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('isEmergency')}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700 flex items-center">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1 text-red-500" />
                Emergency Leave
              </label>
            </div>
          </div>

          {/* Leave Balance Display */}
          {selectedBalance && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Leave Balance</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Total: </span>
                  <span className="font-semibold">{selectedBalance.totalDays} days</span>
                </div>
                <div>
                  <span className="text-blue-700">Used: </span>
                  <span className="font-semibold">{selectedBalance.daysTaken} days</span>
                </div>
                <div>
                  <span className="text-blue-700">Remaining: </span>
                  <span className={`font-semibold ${selectedBalance.daysRemaining < 5 ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedBalance.daysRemaining} days
                  </span>
                </div>
              </div>
              {selectedBalance.daysPending > 0 && (
                <div className="mt-2 text-sm text-yellow-700">
                  <span className="font-medium">Pending: </span>
                  {selectedBalance.daysPending} days
                </div>
              )}
            </div>
          )}

          {/* Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                {...register('startDate')}
                min={new Date().toISOString().split('T')[0]}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.startDate ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                {...register('endDate')}
                min={watchedValues[0] || new Date().toISOString().split('T')[0]}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.endDate ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
              )}
            </div>
          </div>

          {/* Calculated Days and Warning */}
          {calculatedDays > 0 && (
            <div className={`p-4 rounded-md ${exceedsBalance ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center">
                <span className="text-sm font-medium">
                  Total Days Requested: <span className="text-lg">{calculatedDays}</span>
                </span>
                {exceedsBalance && (
                  <span className="ml-4 text-sm text-red-600 font-medium">
                    ⚠️ Exceeds available balance!
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Leave *
            </label>
            <textarea
              {...register('reason')}
              rows={3}
              placeholder="Please provide a detailed reason for your leave request..."
              className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.reason ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.reason && (
              <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>
            )}
          </div>

          {/* Emergency Reason (conditional) */}
          {watchedValues[2] && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emergency Details *
              </label>
              <textarea
                {...register('emergencyReason')}
                rows={2}
                placeholder="Please explain the emergency situation..."
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.emergencyReason ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.emergencyReason && (
                <p className="mt-1 text-sm text-red-600">{errors.emergencyReason.message}</p>
              )}
            </div>
          )}

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Handover Notes
              </label>
              <textarea
                {...register('handoverNotes')}
                rows={3}
                placeholder="Work handover instructions for colleagues..."
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.handoverNotes ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.handoverNotes && (
                <p className="mt-1 text-sm text-red-600">{errors.handoverNotes.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emergency Contact During Leave
              </label>
              <input
                type="text"
                {...register('contactDuringLeave')}
                placeholder="Alternative contact number or email..."
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.contactDuringLeave ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.contactDuringLeave && (
                <p className="mt-1 text-sm text-red-600">{errors.contactDuringLeave.message}</p>
              )}
            </div>
          </div>

          {/* File Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supporting Documents
              {selectedLeaveType?.requiresDocuments && <span className="text-red-500"> *</span>}
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                id="attachments"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
              <label
                htmlFor="attachments"
                className="cursor-pointer flex flex-col items-center justify-center text-center"
              >
                <PaperClipIcon className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  Click to upload or drag and drop
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  PDF, DOC, JPG, PNG (max 10MB per file)
                </span>
              </label>
            </div>

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {selectedLeaveType?.requiresDocuments && selectedFiles.length === 0 && (
              <p className="mt-1 text-sm text-red-600">
                Supporting documents are required for this leave type
              </p>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || (selectedLeaveType?.requiresDocuments && selectedFiles.length === 0)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Submitting...' : (editMode || isEdit ? 'Update Request' : 'Submit Request')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default LeaveRequestForm; 